import { Post } from "../models/Post";

type TimelinePost = Pick<
  Post,
  | "id"
  | "createdAt"
  | "generationSource"
  | "generationRequestId"
  | "generationSourceCreatedAt"
>;

export type TimelineUpperBound = TimelinePost;

export const MANUAL_ROW_POST_LIMIT = 12;
export const LEGACY_ROW_POST_LIMIT = 12;
const LEGACY_GENERATION_GAP_MS = 30 * 60 * 1000;

function legacySource(post: TimelinePost): string | undefined {
  const source = post.generationSource?.trim();
  return source && source !== "unknown" ? source : undefined;
}

export function draftTimelineAt(post: TimelinePost): number {
  return Math.min(
    post.generationSourceCreatedAt ?? post.createdAt,
    post.createdAt,
  );
}

export function compareDraftTimelinePosts(
  a: TimelinePost,
  b: TimelinePost,
): number {
  return draftTimelineAt(b) - draftTimelineAt(a) || b.id.localeCompare(a.id);
}

/**
 * The smallest unit pagination may safely split around. A generation request
 * can contain several article rows that are created concurrently, so the
 * whole request is completed together. Legacy generated posts fall back to
 * their source and creation-time proximity, while adjacent source-less posts
 * form the manual batch between generation requests.
 */
export function draftPaginationUnitKeys(
  posts: readonly TimelinePost[],
): Map<string, string> {
  const units = new Map<string, string>();
  const legacyPostsBySource = new Map<string, TimelinePost[]>();

  posts.forEach((post) => {
    if (post.generationRequestId) {
      units.set(post.id, `request:${post.generationRequestId}`);
      return;
    }

    const source = legacySource(post);
    if (!source) {
      units.set(post.id, "manual");
      return;
    }

    const sourcePosts = legacyPostsBySource.get(source) || [];
    sourcePosts.push(post);
    legacyPostsBySource.set(source, sourcePosts);
  });

  legacyPostsBySource.forEach((sourcePosts, source) => {
    let sessionAnchorTimelineAt: number | undefined;
    let previousTimelineAt: number | undefined;
    let sessionSize = 0;

    [...sourcePosts].sort(compareDraftTimelinePosts).forEach((post) => {
      const timelineAt = draftTimelineAt(post);
      if (
        sessionAnchorTimelineAt === undefined ||
        previousTimelineAt === undefined ||
        previousTimelineAt - timelineAt > LEGACY_GENERATION_GAP_MS ||
        sessionSize >= LEGACY_ROW_POST_LIMIT
      ) {
        sessionAnchorTimelineAt = timelineAt;
        sessionSize = 0;
      }
      units.set(post.id, `source:${source}:session:${sessionAnchorTimelineAt}`);
      previousTimelineAt = timelineAt;
      sessionSize += 1;
    });
  });

  return units;
}

export function draftPaginationUnitKey(post: TimelinePost): string {
  return draftPaginationUnitKeys([post]).get(post.id) || "manual";
}

function paginationUnitFor(
  post: TimelinePost,
  units: ReadonlyMap<string, string>,
): string {
  return units.get(post.id) || draftPaginationUnitKey(post);
}

function isManualPost(post: TimelinePost): boolean {
  return !post.generationRequestId && !legacySource(post);
}

function legacySessionCompletionThreshold(
  posts: readonly TimelinePost[],
): number {
  return Math.min(...posts.map(draftTimelineAt)) - LEGACY_GENERATION_GAP_MS;
}

function timelinePrecedes(
  firstUnseen: TimelineUpperBound,
  cutoff: TimelinePost,
) {
  return (
    firstUnseen.createdAt < draftTimelineAt(cutoff) ||
    (firstUnseen.createdAt === draftTimelineAt(cutoff) &&
      firstUnseen.id < cutoff.id)
  );
}

function hasMoreAfterStableWindow<T extends TimelinePost>(
  fetchedAll: boolean,
  orderedPosts: T[],
  posts: T[],
): boolean {
  return fetchedAll ? orderedPosts.length > posts.length : true;
}

function completeVisibleLegacyUnits<T extends TimelinePost>(
  orderedPosts: T[],
  visibleLegacyUnits: ReadonlySet<string>,
  units: ReadonlyMap<string, string>,
  fetchedAll: boolean,
  firstUnseen?: TimelineUpperBound,
): boolean {
  return Array.from(visibleLegacyUnits).every((unit) => {
    const unitPosts = orderedPosts.filter(
      (post) => paginationUnitFor(post, units) === unit,
    );
    return (
      unitPosts.length >= LEGACY_ROW_POST_LIMIT ||
      fetchedAll ||
      Boolean(
        firstUnseen &&
          firstUnseen.createdAt < legacySessionCompletionThreshold(unitPosts),
      )
    );
  });
}

function visibleUnits(
  posts: readonly TimelinePost[],
  units: ReadonlyMap<string, string>,
  prefix: string,
): Set<string> {
  return new Set(
    posts
      .map((post) => paginationUnitFor(post, units))
      .filter((unit) => unit.startsWith(prefix)),
  );
}

function visibleRequestIds(posts: readonly TimelinePost[]): Set<string> {
  return new Set(
    posts.flatMap((post) =>
      post.generationRequestId ? [post.generationRequestId] : [],
    ),
  );
}

function completeManualBoundary<T extends TimelinePost>(
  orderedPosts: T[],
  targetEnd: number,
): { end: number; size: number } {
  let end = targetEnd;
  let contiguousManualPosts = 0;

  for (let index = targetEnd - 1; index >= 0; index -= 1) {
    if (!isManualPost(orderedPosts[index])) break;
    contiguousManualPosts += 1;
  }

  // Manual rows are chunked in groups of 12. If an unusual limit (for
  // example, active pending-post tracking) lands in a later chunk, complete
  // that chunk rather than treating the entire contiguous run as one row.
  let size =
    contiguousManualPosts === 0
      ? 0
      : ((contiguousManualPosts - 1) % MANUAL_ROW_POST_LIMIT) + 1;

  while (
    size < MANUAL_ROW_POST_LIMIT &&
    end < orderedPosts.length &&
    isManualPost(orderedPosts[end])
  ) {
    end += 1;
    size += 1;
  }

  return { end, size };
}

export function selectDraftTimelineWindow<T extends TimelinePost>(
  fetchedPosts: T[],
  visibleLimit: number,
  fetchedAll: boolean,
  firstUnseen?: TimelineUpperBound,
  completedRequestIds: ReadonlySet<string> = new Set(),
): { posts: T[]; stable: boolean; hasMore: boolean } {
  const orderedPosts = [...fetchedPosts].sort(compareDraftTimelinePosts);
  const paginationUnits = draftPaginationUnitKeys(orderedPosts);
  const targetEnd = Math.min(visibleLimit, orderedPosts.length);
  const cutoff = orderedPosts[targetEnd - 1];
  const boundaryIsManual = Boolean(cutoff && isManualPost(cutoff));
  const nominalPosts = orderedPosts.slice(0, targetEnd);
  const generatedUnitsToComplete = visibleUnits(
    nominalPosts,
    paginationUnits,
    "request:",
  );
  const requestIdsToComplete = visibleRequestIds(nominalPosts);
  const visibleLegacyUnits = visibleUnits(
    nominalPosts,
    paginationUnits,
    "source:",
  );
  const manualBoundary = completeManualBoundary(orderedPosts, targetEnd);

  // Manual rows are deliberately bounded by generated rows, so only complete
  // the contiguous manual batch when the nominal cutoff lands inside it.
  const manualBatchEnd = boundaryIsManual ? manualBoundary.end : targetEnd;

  // Include every fetched member of each visible generation, even if sources
  // within that generation were created concurrently and interleaved.
  const posts = orderedPosts.filter(
    (post, index) =>
      index < manualBatchEnd ||
      generatedUnitsToComplete.has(paginationUnitFor(post, paginationUnits)) ||
      visibleLegacyUnits.has(paginationUnitFor(post, paginationUnits)),
  );

  const timelineIsStable = Boolean(
    fetchedAll ||
      (cutoff && firstUnseen && timelinePrecedes(firstUnseen, cutoff)),
  );
  const requestUnitsAreComplete = Array.from(requestIdsToComplete).every(
    (requestId) => completedRequestIds.has(requestId),
  );
  const legacyUnitsAreComplete = completeVisibleLegacyUnits(
    orderedPosts,
    visibleLegacyUnits,
    paginationUnits,
    fetchedAll,
    firstUnseen,
  );
  const manualBatchIsComplete = Boolean(
    !boundaryIsManual ||
      manualBoundary.size >= MANUAL_ROW_POST_LIMIT ||
      fetchedAll ||
      (firstUnseen && !isManualPost(firstUnseen)),
  );
  // Client-side exclusions (e.g. publishing failures) must not consume the
  // visible page budget. Keep fetching until it is filled or exhausted.
  const stable =
    (fetchedAll || orderedPosts.length >= visibleLimit) &&
    timelineIsStable &&
    requestUnitsAreComplete &&
    legacyUnitsAreComplete &&
    manualBatchIsComplete;

  return {
    posts,
    stable,
    hasMore:
      stable && hasMoreAfterStableWindow(fetchedAll, orderedPosts, posts),
  };
}
